import type { PriceKey } from '@/constants/pricing'
import type { Quality, Resolution } from '@/constants/render'

export interface User {
  id: string
  email: string
  is_active: boolean
  credits: number
  created_date: string
  last_login_date: string | null
}

export interface CodeRequestPayload {
  email: string
}

export interface VerifyPayload {
  email: string
  code: string
}

export type VerifyResult =
  | { status: 'signed_in'; user: User }
  | { status: 'consent_required'; ticket: string }

export interface RegisterPayload {
  ticket: string
  accepted_kvkk: boolean
  accepted_mesafeli: boolean
}

export type InvoiceKind = 'personal' | 'company'

export interface InvoiceAddress {
  id: string
  label: string
  kind: InvoiceKind
  name: string
  tax_id: string
  phone: string
  address: string
}

export type InvoiceAddressPayload = Omit<InvoiceAddress, 'id'>

export interface CreditPack {
  id: string
  name: string
  credits: number
  amount_minor: number
  accent: string | null
}

export interface CheckoutPayload {
  pack_id: string
  address_id: string
  accepted_kvkk: boolean
  accepted_mesafeli: boolean
}

export interface CheckoutSession {
  payment_id: string
  iframe_url: string
}

export type PaymentOutcome = 'paid' | 'failed'

export interface PaymentSettled {
  payment_id: string
  reference: string
  status: PaymentOutcome
  credits: number
  balance: number
  failed_reason: string | null
}

export const FILE_SOURCE = { uploaded: 1, linked: 2 } as const
export type FileSource = (typeof FILE_SOURCE)[keyof typeof FILE_SOURCE]

export const FILE_STATUS = { ready: 1, pending: 2, failed: 3 } as const
export type FileStatus = (typeof FILE_STATUS)[keyof typeof FILE_STATUS]

export const FILE_CATEGORY = { model: 1, render: 2, ar: 3, watermark: 4 } as const
export type FileCategory = (typeof FILE_CATEGORY)[keyof typeof FILE_CATEGORY]

export interface FileSettings {
  smooth_shading: boolean
  smoothing_angle: number
  rotation: { x: number; y: number; z: number }
  custom_scale: number
}

export interface UserFile {
  id: string
  filename: string
  extension: string
  size_bytes: number
  source: FileSource
  status: FileStatus
  category: FileCategory
  has_thumbnail: boolean
  file_settings: unknown
  material_overrides: unknown
  created_date: string
}

export interface CatalogEntry {
  id: number
  name: string
  label: string
  bound?: number
}

export interface MaterialParams {
  Color: [number, number, number]
  Metallic: number
  Roughness: number
  Glass: number
  Scale: number
  Alpha: number
}

export type MaterialParam = keyof MaterialParams

export type NumericMaterialParam = Exclude<MaterialParam, 'Color'>

export interface MaterialCatalogEntry extends CatalogEntry, MaterialParams {
  preview_file_id: string | null
}

export interface BlenderCatalog {
  id: string | null
  name: string | null
  materials: MaterialCatalogEntry[]
  scenes: CatalogEntry[]
}

export interface UserMaterial extends MaterialParams {
  id: string
  name: string
  base: number
  preview_file_id: string | null
}

export type UserMaterialPayload = Omit<UserMaterial, 'id' | 'preview_file_id'> & {
  id?: string
}

export type MaterialOverrides = Record<string, string>

export const JOB_STATUS = { queued: 1, running: 2, done: 3, failed: 4 } as const
export type JobStatus = (typeof JOB_STATUS)[keyof typeof JOB_STATUS]

export interface Job {
  id: string
  file_id: string | null
  kind: string
  filename: string
  status: JobStatus
  credits_cost: number
  started_date: string | null
  created_date: string
}

export const NOTIFICATION_KIND = { done: 1, failed: 2 } as const
export type NotificationKind = (typeof NOTIFICATION_KIND)[keyof typeof NOTIFICATION_KIND]

export interface Notification {
  id: string
  kind: NotificationKind
  job_kind: string
  filename: string
  message: string | null
  read_date: string | null
  created_date: string
}

export interface LedgerEntry {
  id: string
  kind: PriceKey
  credits: number
  created_date: string
}

export interface UsageCursor {
  cursor_date: string
  cursor_id: string
}

export interface UsagePage {
  items: LedgerEntry[]
  total_credits: number
  has_more: boolean
}

export interface UsageFilter {
  start: string
  end: string
}

export interface CameraView {
  location: [number, number, number]
  rotation: [number, number, number]
  fov: number
}

export interface RenderPayload {
  file_ids: string[]
  scene: string
  quality: Quality
  resolution: Resolution
  watermark: string | null
  camera?: CameraView | null
}

export interface ConvertPayload {
  ids: string[]
  format: string
}

export interface IdsPayload {
  ids: string[]
}

export interface Skipped {
  filename: string
  reason: string
}

export interface Batch<T> {
  items: T[]
  skipped: Skipped[]
}

export interface BridgeStatus {
  online: boolean
  device_name: string | null
}

export interface Price {
  key: PriceKey
  credits: number
  description: string
}

export interface BridgeStatusResponse extends BridgeStatus {
  download: boolean
}

export interface PairCode {
  expires_date: string
}

export interface ArLink {
  token: string
  expires_in: number
}

export interface ArMeta {
  filename: string
  expires_in: number
}
